import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Building, Mail, Phone, MapPin, Users, Calendar, 
  Award, TrendingUp, CheckCircle, ArrowLeft, Globe
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

const OrganizationDetails = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchOrganizationDetails();
  }, [orgId]);

  const fetchOrganizationDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/organizations/${orgId}`);
      const data = await response.json();
      
      // Check if response has error
      if (data.detail || !data.id) {
        setOrganization(null);
      } else {
        setOrganization(data);
      }
    } catch (error) {
      console.error('Error fetching organization details:', error);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      approved: { color: 'bg-green-100 text-green-800 border-green-300', label: 'Approved' },
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Pending' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-300', label: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold border-2 ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getTierBadge = (tier) => {
    if (!tier) return null;
    const badges = {
      Luminary: { color: 'bg-yellow-100 text-yellow-800 border-yellow-500', icon: '🏆' },
      Inspired: { color: 'bg-gray-100 text-gray-800 border-gray-500', icon: '⚡' },
      Aspiring: { color: 'bg-orange-100 text-orange-800 border-orange-500', icon: '🎯' }
    };
    const badge = badges[tier];
    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 ${badge.color}`}>
        <span className="text-lg">{badge.icon}</span>
        {tier}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization details...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Organization Not Found</h3>
          <p className="text-gray-600 mb-6">The organization you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/admin/user-management')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
          >
            Back to User Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <SystemAdminLayout>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-2xl">
        <div className="px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/admin/user-management')}
              className="p-2 hover:bg-white/20 rounded-lg transition-all"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-black">{organization.name}</h1>
              <p className="text-blue-100">Organization Details</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(organization.status)}
            {organization.certification_tier && getTierBadge(organization.certification_tier)}
            {organization.organization_size && (
              <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold">
                {organization.organization_size}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-black text-gray-900 mb-6">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Company Size</p>
                    <p className="text-lg font-bold text-gray-900">{organization.organization_size || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Industry</p>
                    <p className="text-lg font-bold text-gray-900">{organization.industry || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Certification Tier</p>
                    <p className="text-lg font-bold text-gray-900">{organization.certification_tier || 'Not Certified'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Registered</p>
                    <p className="text-lg font-bold text-gray-900">
                      {organization.created_at ? new Date(organization.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Certification Score - if available */}
              {organization.overall_score > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Certification Score</p>
                      <p className="text-3xl font-black text-blue-600">{organization.overall_score}/100</p>
                    </div>
                    <div className="w-20 h-20">
                      <svg className="transform -rotate-90 w-20 h-20">
                        <circle cx="40" cy="40" r="35" stroke="#e5e7eb" strokeWidth="6" fill="none"/>
                        <circle 
                          cx="40" cy="40" r="35" 
                          stroke="#3b82f6" 
                          strokeWidth="6" 
                          fill="none"
                          strokeDasharray={`${organization.overall_score * 2.2} 220`}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-black text-gray-900 mb-6">Contact Information</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <a href={`mailto:${organization.email}`} className="text-blue-600 hover:underline font-semibold">
                    {organization.email}
                  </a>
                </div>
                {organization.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <a href={organization.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">
                      {organization.website}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {organization.description && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-black text-gray-900 mb-4">About</h2>
                <p className="text-gray-700 leading-relaxed">{organization.description}</p>
              </div>
            )}

            {/* Assessment History */}
            {organization.assessments && organization.assessments.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-black text-gray-900 mb-6">Assessment History</h2>
                <div className="space-y-4">
                  {organization.assessments.map((assessment, index) => (
                    <div key={index} className="border-2 border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-gray-900">{assessment.assessment_id}</p>
                          <p className="text-sm text-gray-600">
                            Status: {assessment.overall_status}
                          </p>
                        </div>
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Stats & Certification */}
          <div className="space-y-6">
            {/* Certification Card */}
            {organization.certification_tier && (
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-500 rounded-2xl p-8">
                <div className="text-center">
                  <Award className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-black text-gray-900 mb-2">
                    {organization.certification_tier}
                  </h3>
                  <p className="text-gray-700 mb-4">Certification Tier</p>
                  {organization.overall_score > 0 && (
                    <div className="bg-white rounded-xl p-4 border-2 border-yellow-500">
                      <p className="text-4xl font-black text-yellow-600">
                        {organization.overall_score}
                      </p>
                      <p className="text-sm text-gray-600">out of 100</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-xl font-black text-gray-900 mb-6">Quick Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="font-bold capitalize">{organization.status}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Company Size</span>
                  <span className="font-bold">{organization.organization_size || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Industry</span>
                  <span className="font-bold">{organization.industry || 'N/A'}</span>
                </div>
                {organization.certification_tier && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Tier</span>
                    <span className="font-bold">{organization.certification_tier}</span>
                  </div>
                )}
                {organization.overall_score > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Score</span>
                    <span className="font-bold text-blue-600">{organization.overall_score}/100</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </SystemAdminLayout>
  );
};

export default OrganizationDetails;
